/**
 * @module lib/ai/geminiClient
 * @description Cliente singleton de Google Gemini y control de cuota diaria.
 *
 * Gestiona la inicialización única de `GoogleGenerativeAI` y expone funciones
 * de utilidad compartidas por `assistantGemini.ts` y `acsAI.ts`.
 *
 * ## Singleton y API key
 * El cliente se crea una sola vez y se reutiliza en llamadas sucesivas.
 * La API key se obtiene de `VITE_GEMINI_API_KEY` (variable de entorno) o puede
 * pasarse explícitamente al llamar `getGeminiClient(apiKey)` — útil para
 * configuración dinámica desde el panel de Settings.
 *
 * ## Control de cuota
 * `incrementUsageCount()` y `hasReachedDailyLimit()` implementan un contador
 * en memoria que se resetea automáticamente a medianoche UTC. Esto protege
 * contra bucles que agoten la cuota gratuita de Gemini.
 *
 * ## Modelo por defecto
 * `gemini-2.5-flash` — modelo rápido con ventana de 1M tokens, ideal para
 * análisis de controles largos y conversaciones con historial extenso.
 *
 * @see assistantGemini.ts que usa este cliente para el chat y análisis de gaps
 * @see acsAI.ts que usa este cliente para el asistente normativo ACS
 */
// =====================================================
// CLIENTE DE GOOGLE GEMINI - ASISTENTE DE CUMPLIMIENTO ISO
// =====================================================

import { GoogleGenerativeAI } from '@google/generative-ai';
import type { AIConfig } from '../../types/ai';

// Singleton del cliente Gemini
let geminiClient: GoogleGenerativeAI | null = null;
// Flag para loguear el warning de API key faltante solo una vez
let apiKeyWarningLogged = false;

/**
 * Obtener o crear cliente de Gemini
 */
export function getGeminiClient(apiKey?: string): GoogleGenerativeAI | null {
  // Si ya existe y no se proporciona nueva key, retornar existente
  if (geminiClient && !apiKey) {
    return geminiClient;
  }

  // Obtener API key desde env o parámetro
  const key = apiKey || import.meta.env.VITE_GEMINI_API_KEY;

  if (!key) {
    if (!apiKeyWarningLogged) {
      console.warn('[AI] ⚠️ Gemini API key no configurada');
      apiKeyWarningLogged = true;
    }
    return null;
  }

  try {
    geminiClient = new GoogleGenerativeAI(key);
    console.log('[AI] ✅ Cliente Gemini inicializado');
    return geminiClient;
  } catch (error) {
    console.error('[AI] ❌ Error inicializando Gemini:', error);
    return null;
  }
}

/**
 * Configuración por defecto
 */
export const DEFAULT_AI_CONFIG: AIConfig = {
  provider: 'gemini',
  model: 'gemini-2.5-flash', // Modelo rápido y gratuito (soporta 1M tokens)
  temperature: 0.7,
  maxTokens: 2048,
  enableRAG: true,
  enableCache: true
};

/**
 * Verificar si el cliente está configurado
 */
export function isAIConfigured(): boolean {
  // Check if client already exists OR if API key is available in env
  const hasClient = getGeminiClient() !== null;
  const hasEnvKey = !!import.meta.env.VITE_GEMINI_API_KEY;
  const hasSavedKey = !!getSavedAPIKey();

  return hasClient || hasEnvKey || hasSavedKey;
}

/**
 * Guardar API key de forma segura (en localStorage con advertencia)
 */
export function saveAPIKey(apiKey: string): void {
  if (!apiKey) return;

  // ADVERTENCIA: Guardar en localStorage NO es seguro para producción
  // En producción debería manejarse en backend
  console.warn('[AI] ⚠️ Guardando API key en localStorage (solo para desarrollo)');
  localStorage.setItem('gemini_api_key', apiKey);

  // Reinicializar cliente
  geminiClient = null;
  getGeminiClient(apiKey);
}

/**
 * Obtener API key guardada
 */
export function getSavedAPIKey(): string | null {
  return localStorage.getItem('gemini_api_key');
}

/**
 * Eliminar API key
 */
export function removeAPIKey(): void {
  localStorage.removeItem('gemini_api_key');
  geminiClient = null;
  console.log('[AI] 🗑️ API key eliminada');
}

/**
 * Test de conexión con Gemini
 */
export async function testConnection(): Promise<boolean> {
  const client = getGeminiClient();
  if (!client) return false;

  try {
    const model = client.getGenerativeModel({ model: DEFAULT_AI_CONFIG.model });
    const result = await model.generateContent('Test connection');
    const response = await result.response;
    const text = response.text();

    console.log('[AI] ✅ Conexión con Gemini exitosa');
    return !!text;
  } catch (error) {
    console.error('[AI] ❌ Error de conexión con Gemini:', error);
    return false;
  }
}

// =====================================================
// CONTADOR DE CONSULTAS
// =====================================================

interface UsageStats {
  date: string; // YYYY-MM-DD
  count: number;
}

const STORAGE_KEY = 'gemini_usage_stats';
const DAILY_LIMIT = 50; // Límite conservador (Gemini permite 1500/día gratis)

/**
 * Obtener estadísticas de uso
 */
export function getUsageStats(): UsageStats {
  const today = new Date().toISOString().split('T')[0];
  const stored = localStorage.getItem(STORAGE_KEY);

  if (!stored) {
    return { date: today, count: 0 };
  }

  try {
    const stats: UsageStats = JSON.parse(stored);

    // Si es un día diferente, resetear el contador
    if (stats.date !== today) {
      return { date: today, count: 0 };
    }

    return stats;
  } catch {
    return { date: today, count: 0 };
  }
}

/**
 * Incrementar contador de consultas
 */
export function incrementUsageCount(): void {
  const stats = getUsageStats();
  stats.count += 1;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));

  console.log(`[AI] 📊 Consultas hoy: ${stats.count}/${DAILY_LIMIT}`);
}

/**
 * Verificar si se alcanzó el límite diario
 */
export function hasReachedDailyLimit(): boolean {
  const stats = getUsageStats();
  return stats.count >= DAILY_LIMIT;
}

/**
 * Obtener consultas restantes
 */
export function getRemainingQueries(): number {
  const stats = getUsageStats();
  return Math.max(0, DAILY_LIMIT - stats.count);
}

/**
 * Resetear contador (solo para testing)
 */
export function resetUsageStats(): void {
  localStorage.removeItem(STORAGE_KEY);
  console.log('[AI] 🔄 Estadísticas de uso reseteadas');
}
